import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { Box, Tooltip } from '@mui/material';
import { FC, ReactNode } from 'react';
import {
  NEWLY_ASSIGNED_STOP_BG,
  NEWLY_ASSIGNED_STOP_HOVER_BG,
} from 'src/components/dispatch/vehicleCardStyles';

import { ExpandedStopVM } from './buildExpandedRowVM';

/**
 * 地点の警告。理由は Tooltip で見せるが、aria-label にも同じ文言を置く
 * （Tooltip はホバーしないと読めず、テストと支援技術から辿れない）。
 */
const StopWarning: FC<{ title: string }> = ({ title }) => (
  <Tooltip title={<Box component="span" sx={{ fontSize: 12 }}>{title}</Box>} placement="top" enterDelay={120}>
    <Box
      component="span"
      aria-label={title}
      sx={{ display: 'inline-flex', flexShrink: 0, color: 'hsl(var(--status-error-accent))' }}
    >
      <WarningAmberRounded sx={{ fontSize: 15 }} />
    </Box>
  </Tooltip>
);

type Props = {
  stop: ExpandedStopVM;
  /** 住所。同名の拠点を見分けるために出す。引けなければ行ごと出さない */
  address?: string;
  /** 未指定なら行はクリックできない（P2b の onShowDetail と同じ規則） */
  onClick?: () => void;
  /** 左端に置く並べ替えハンドル。未指定なら場所だけ空ける */
  dragHandle?: ReactNode;
  /** 直前の自動配車で新しく入った停車地か。背景で示す */
  isNewlyAssigned?: boolean;
};

/**
 * 種別ごとの配色。サンプル (route-planner: pages/MapPage.tsx の PickupRow:249,258 /
 * SortableDeliveryItem:450,472) の値をそのまま使う。
 * `dot` は左列の丸バッジ、`badge*` は名前の前に置く「積地 / 降地」ラベル。
 */
const KIND_STYLE: Record<ExpandedStopVM['kind'], {
  label: string;
  dot: string;
  badgeText: string;
  badgeBorder: string;
  badgeBg: string;
  rowTint: string;
}> = {
  loading: {
    label: '積地',
    dot: 'hsl(142 70% 40%)',
    badgeText: 'hsl(142 70% 35%)',
    badgeBorder: 'hsl(142 70% 45% / 0.35)',
    badgeBg: 'hsl(142 70% 45% / 0.15)',
    // 行の色塗りはしない（利用者の指摘 2026/08/20）。積地はバッジと丸バッジの緑で判別できる
    rowTint: 'transparent',
  },
  unloading: {
    label: '降地',
    dot: 'hsl(210 80% 50%)',
    badgeText: 'hsl(210 80% 50%)',
    badgeBorder: 'hsl(210 80% 50% / 0.35)',
    badgeBg: 'hsl(210 80% 50% / 0.14)',
    rowTint: 'transparent',
  },
};

/** 左端。停車地の並べ替えを入れるときのハンドル位置を今から空けておく */
const HANDLE_WIDTH = 12;

/**
 * 1 停車地の行。kind でバッジと色を出し分ける。
 * 積地・降地・発地で 3 コンポーネントに分けないのは、同じレイアウトが 3 箇所に
 * 重複して幅調整のたびに 3 箇所直すことになるため（発地だけは待機時間も容量も
 * 無く構造が違うので ExpandedRowPanel 内に直接書く）。
 */
export const ExpandedStopRow: FC<Props> = ({ stop, address, onClick, dragHandle, isNewlyAssigned }) => {
  const style = KIND_STYLE[stop.kind];

  return (
    <Box
      data-testid={`expanded-stop-${stop.stopId}`}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 1.25,
        py: 0.75,
        borderBottom: '1px solid hsl(var(--border) / 0.2)',
        '&:last-of-type': { borderBottom: 0 },
        // 種別の色より新規を優先する。両方出すと積地の緑と混ざって
        // どちらの意味か読めない
        bgcolor: isNewlyAssigned ? NEWLY_ASSIGNED_STOP_BG : style.rowTint,
        cursor: onClick ? 'pointer' : 'default',
        // hover もこの要素の bgcolor に乗るので、ハイライト中に既定の色を当てると
        // カーソルを乗せた行だけハイライトが消える
        ...(onClick
          ? {
            '&:hover': {
              bgcolor: isNewlyAssigned ? NEWLY_ASSIGNED_STOP_HOVER_BG : 'hsl(var(--secondary) / 0.15)',
            },
          }
          : {}),
      }}
    >
      {/* 掴む場所を限定しないと、行を選ぼうとしただけでドラッグが始まる。
          並べ替えできない画面でも同じ幅を空けて、行頭の位置を揃える */}
      {dragHandle ?? <Box sx={{ width: HANDLE_WIDTH, flexShrink: 0 }} aria-hidden="true" />}
      {/* 時刻と番号は同じ列に積む。行の右端に時刻を置くと、番号と離れて
          「何時に何番目へ行くか」を目で追えない */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', pt: '2px', flexShrink: 0 }}>
        <Box component="span" sx={{ fontSize: 10, fontFamily: 'monospace', color: 'hsl(var(--muted-foreground))' }}>
          {stop.time}
        </Box>
        <Box
          data-testid="stop-index-badge"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18,
            borderRadius: '9999px',
            fontSize: 10,
            fontWeight: 700,
            color: '#fff',
            bgcolor: style.dot,
          }}
        >
          {stop.seriesIndex}
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              whiteSpace: 'nowrap',
              fontSize: 9,
              fontWeight: 700,
              color: style.badgeText,
              px: 0.5,
              py: '1px',
              borderRadius: '4px',
              border: `1px solid ${style.badgeBorder}`,
              bgcolor: style.badgeBg,
            }}
          >
            {style.label}
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: 'hsl(var(--foreground))',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {stop.locationName}
          </Box>
          {/* 地点ごとの違反。V3Plans も地点カードに出している
              (PlanningsPlace.presenter.tsx:293-302)。積地・降地のどちらにも出す */}
          {stop.hasAlert && <StopWarning title="指定時間が超過しています。" />}
          {stop.hasBerthAlert && <StopWarning title="バース予約の時間外です。" />}
        </Box>
        {address && (
          <Box
            data-testid="expanded-stop-address"
            sx={{
              fontSize: 10,
              color: 'hsl(var(--foreground))',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mt: '2px',
            }}
          >
            {address}
          </Box>
        )}
        {/* 「·」は疑似要素で置く。テキストとして書くと textContent に混ざり、
            読み上げでも「なかぐろ」と読まれる */}
        <Box
          data-testid="stop-metrics"
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '4px 8px',
            mt: '3px',
            fontSize: 10,
            color: 'hsl(var(--foreground))',
          }}
        >
          <Box component="span">待機: {stop.waitMinutes}分</Box>
          <Box component="span" sx={{ '&::before': { content: '"·"', mr: '6px', color: 'hsl(var(--foreground) / 0.35)' } }}>
            作業: {stop.workMinutes}分
          </Box>
          {stop.metricText && (
            <Box
              component="span"
              sx={{
                fontFamily: 'monospace',
                fontVariantNumeric: 'tabular-nums',
                '&::before': { content: '"·"', mr: '6px', color: 'hsl(var(--foreground) / 0.35)' },
              }}
            >
              {stop.metricText}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
